import type { AnchoredRow } from '../types/anchoredRowTypes'
import {
  getLedgerSafeWitnessWindowText
} from './ledgerSafeEditSuggestionRows'

const ARABIC_DIACRITICS_PATTERN = /[\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed]/g

export const MIN_LEDGER_SAFE_WINDOW_CONTENT_SCORE = 0.44
export const MIN_LEDGER_SAFE_SINGLE_ROW_CONTENT_SCORE = 0.52
export const DEFAULT_MAX_LEDGER_SAFE_SUGGESTIONS_PER_EVENT = 5

export const describeLedgerSafeSuggestionContentMatch = (contentScore: number): string =>
  `Local text match ${Math.round(contentScore * 100)}%.`

export const normalizeLedgerSafeSuggestionText = (text: string): string =>
  text
    .normalize('NFKC')
    .toLowerCase()
    .replace(ARABIC_DIACRITICS_PATTERN, '')
    .replace(/[إأٱآا]/g, 'ا')
    .replace(/[ى]/g, 'ي')
    .replace(/[ة]/g, 'ه')
    .replace(/[ؤئ]/g, 'ء')
    .replace(/ـ/g, '')
    .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const tokenizeLedgerSafeSuggestionText = (text: string): string[] =>
  normalizeLedgerSafeSuggestionText(text).split(' ').filter(Boolean)

export const buildLedgerSafeSuggestionCharacterBigrams = (text: string): string[] => {
  const compact = normalizeLedgerSafeSuggestionText(text).replace(/\s+/g, '')
  if (compact.length < 2) {
    return compact ? [compact] : []
  }
  const bigrams: string[] = []
  for (let index = 0; index < compact.length - 1; index += 1) {
    bigrams.push(compact.slice(index, index + 2))
  }
  return bigrams
}

export const scoreLedgerSafeSuggestionMultisetDice = (leftItems: string[], rightItems: string[]): number => {
  if (leftItems.length === 0 || rightItems.length === 0) {
    return 0
  }
  const rightCounts = new Map<string, number>()
  rightItems.forEach(item => {
    rightCounts.set(item, (rightCounts.get(item) || 0) + 1)
  })
  let intersection = 0
  leftItems.forEach(item => {
    const count = rightCounts.get(item) || 0
    if (count > 0) {
      intersection += 1
      rightCounts.set(item, count - 1)
    }
  })
  return (2 * intersection) / (leftItems.length + rightItems.length)
}

export const scoreLedgerSafeSuggestionTokenContainment = (leftTokens: string[], rightTokens: string[]): number => {
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }
  const rightSet = new Set(rightTokens)
  const intersection = leftTokens.filter(token => rightSet.has(token)).length
  return intersection / Math.min(leftTokens.length, rightTokens.length)
}

export const scoreLedgerSafeSuggestionTextSimilarity = (leftText: string, rightText: string): number => {
  const leftNormalized = normalizeLedgerSafeSuggestionText(leftText)
  const rightNormalized = normalizeLedgerSafeSuggestionText(rightText)
  if (!leftNormalized || !rightNormalized) {
    return 0
  }
  if (leftNormalized === rightNormalized) {
    return 1
  }
  const leftTokens = leftNormalized.split(' ').filter(Boolean)
  const rightTokens = rightNormalized.split(' ').filter(Boolean)
  const tokenDice = scoreLedgerSafeSuggestionMultisetDice(leftTokens, rightTokens)
  const containment = scoreLedgerSafeSuggestionTokenContainment(leftTokens, rightTokens)
  const characterDice = scoreLedgerSafeSuggestionMultisetDice(
    buildLedgerSafeSuggestionCharacterBigrams(leftNormalized),
    buildLedgerSafeSuggestionCharacterBigrams(rightNormalized)
  )
  return Math.max(tokenDice, (tokenDice + containment) / 2, characterDice * 0.92)
}

export const scoreLedgerSafeWitnessWindowSimilarity = ({
  rows,
  rowIds,
  sourceWitnessId,
  targetWitnessId
}: {
  rows: AnchoredRow[]
  rowIds: string[]
  sourceWitnessId: string
  targetWitnessId: string
}): number =>
  scoreLedgerSafeSuggestionTextSimilarity(
    getLedgerSafeWitnessWindowText(rows, rowIds, sourceWitnessId),
    getLedgerSafeWitnessWindowText(rows, rowIds, targetWitnessId)
  )

export const findLedgerSafeSuggestionRemovedPrefixTokens = (beforeText: string, afterText: string): string[] => {
  const beforeTokens = tokenizeLedgerSafeSuggestionText(beforeText)
  const afterTokens = tokenizeLedgerSafeSuggestionText(afterText)
  let sharedSuffixLength = 0
  while (
    sharedSuffixLength < beforeTokens.length &&
    sharedSuffixLength < afterTokens.length &&
    beforeTokens[beforeTokens.length - 1 - sharedSuffixLength] === afterTokens[afterTokens.length - 1 - sharedSuffixLength]
  ) {
    sharedSuffixLength += 1
  }
  return beforeTokens.slice(0, beforeTokens.length - sharedSuffixLength)
}

export const findLedgerSafeSuggestionRemovedSuffixTokens = (beforeText: string, afterText: string): string[] => {
  const beforeTokens = tokenizeLedgerSafeSuggestionText(beforeText)
  const afterTokens = tokenizeLedgerSafeSuggestionText(afterText)
  let sharedPrefixLength = 0
  while (
    sharedPrefixLength < beforeTokens.length &&
    sharedPrefixLength < afterTokens.length &&
    beforeTokens[sharedPrefixLength] === afterTokens[sharedPrefixLength]
  ) {
    sharedPrefixLength += 1
  }
  return beforeTokens.slice(sharedPrefixLength)
}

export const findLedgerSafeSuggestionAddedPrefixTokens = (beforeText: string, afterText: string): string[] =>
  findLedgerSafeSuggestionRemovedPrefixTokens(afterText, beforeText)

export const findLedgerSafeSuggestionAddedSuffixTokens = (beforeText: string, afterText: string): string[] =>
  findLedgerSafeSuggestionRemovedSuffixTokens(afterText, beforeText)

export const tokenLedgerSafeSuggestionSequenceMatches = (
  candidateTokens: string[],
  expectedTokens: string[]
): boolean => {
  if (expectedTokens.length === 0 || candidateTokens.length < expectedTokens.length) {
    return false
  }
  const candidateText = candidateTokens.join(' ')
  const expectedText = expectedTokens.join(' ')
  return scoreLedgerSafeSuggestionTextSimilarity(candidateText, expectedText) >= 0.74
}
